import PaymentHeader from './PaymentHeader'
import Dialog from '../../../../../components/dialog/Dialog'
import React, { useState } from 'react'
import { OnlinePaymentModal, PaymentFormModel } from '../../../../../types/contracts/contractsModel'
import { onlinePaymentMode } from '../../../../../enums/contractsEnum'
import { FormattedMessage, useIntl } from 'react-intl'
import { ClassValue } from 'classnames/types'
import classNames from 'classnames'
import { useMediaQuery } from 'react-responsive'
import { makeStyles } from '@material-ui/core/styles'
import styles from '../../../../../styles'
import Button from '../../../../../components/button/Button'
import fetchFactory from '../../../../../utils/fetchFactory'
import PaymentForm from './PaymentForm'
import Loader from '../../../../../components/loader/Loader'
import { saveInteractions } from '../../../../gtm/utils/gtmCallback'
import { displaySnackbar } from '../../../../reducers/snackBar/actions'
import { useDispatch } from 'react-redux'
import linksEnum from '../../../../../enums/linksEnum'
import { useHistory } from 'react-router-dom'

const useStyle = makeStyles(
    (theme) => (
        {
            ...styles(theme),
            childContainerLargeScreenStyle: {
                width: '60%',
                maxWidth: 900,
                margin: '25px auto 0'
            },
            childContainerSmallScreenStyle: {
                marginTop: 40
            },
            descriptionStyle: {
                justifyContent: 'center',
                alignItems: 'center'
            },
            infoStyle: {
                color: theme.palette.primary.main,
                fontWeight: 600
            },
            btnStyle: {
                marginTop: 0
            },
            spinnerStyle: {
                textAlign: 'center'
            },
            labelSoldeStyle: {
                marginBottom: 10
            }
        }
    )
)

interface AcceptingProps {
    closeDialog: () => void,
    paymentModal: OnlinePaymentModal
}

type PaymentDialogProps = AcceptingProps

const PaymentDialog: React.FC<PaymentDialogProps> = (
    {
        paymentModal,
        closeDialog
    }
) => {
    const classes = useStyle()
    const intl = useIntl()
    const [loading, setLoading] = useState<boolean>(false)
    const isLargeScreen = useMediaQuery(
        {
            query: '(min-width: 1025px)'
        }
    )
	const dispatch = useDispatch()
	const history = useHistory();

    const childContainerClass: ClassValue = classNames(
        {
            [classes.childContainerLargeScreenStyle]: isLargeScreen,
            [classes.childContainerSmallScreenStyle]: !isLargeScreen
        }
    )

    const descriptionClass: ClassValue = classNames(
        'row',
        classes.descriptionStyle
    )

    const infoClass: ClassValue = classNames(
        classes.text1_opacity,
        classes.infoStyle
    )

    const btnClass: ClassValue = classNames(
        'col-xs-8 col-md-3',
        classes.btnStyle
    )

    const labelSoldeClass: ClassValue = classNames(
        'col-xs-12 col-md-9',
        classes.labelSoldeStyle
    )

    const payment = (amount: number) => {
        const body = JSON.stringify({
            amount,
            num_bail: paymentModal.lease,
            typeAffectation: paymentModal.type === onlinePaymentMode.all ? 'AUTO' : 'MANU',
            pieceNumero: paymentModal.piece_numero,
            target: process.env.REACT_APP_TARGET
        })
        setLoading(true)

        return fetchFactory(`/api/ec_${process.env.REACT_APP_TARGET}/payment/payment`, {
            method: 'POST',
            body
        })
            .then((response: any) => {
                saveInteractions(
                    {
                        category: 'Votre Compte',
                        action: 'Paiement en ligne',
                        label: `Payer en ligne ${paymentModal.type === onlinePaymentMode.all ? 'Solde' : 'Facture'}::OK`
                    }
                )

                // Création du formulaire pour envoi à paybox
                const form = document.createElement('form')
                form.target = '_self'
                form.method = 'POST'
                form.action = response.url
                Object.keys(response.post_data).forEach((key: any) => {
                    const input = document.createElement('input')
                    input.type = 'text'
                    input.name = key
                    input.value = response.post_data[key]
                    form.appendChild(input)
                })
                document.body.appendChild(form)

                window.open('', '_self', 'status=0,title=0,height=600,width=800,scrollbars=1')
                form.submit()
            })
	        .catch(
		        (response: any) => {
			        dispatch(displaySnackbar(
				        {
					        id: 'contracts.account.payment.finished.error.title',
					        message: response.error,
					        open: true,
					        hideIcon: true
				        }
			        ))
			        // Code réponse erreur téléphone paiement, redirection profil.
			        if (response.code === 33) {
				        history.push(linksEnum.profile)
			        }
		        }
	        )
            .finally(() => setLoading(false))
    }

    const validate = (values: PaymentFormModel) => {
        const errors: any = {}

        if (!values.amount) {
            errors.amount = intl.formatMessage(
                {
                    id: 'global.errors.required',
                    defaultMessage: 'Requis*',
                    description: 'Required message'
                }
            )
        } else {
            if (isNaN(Number(values.amount.toString().replace(',', '.')))) {
                errors.amount = intl.formatMessage(
                    {
                        id: 'contracts.account.payment.partial.amountString',
                        defaultMessage: 'Le montant ne peut contenir que des nombres',
                        description: 'String amount error'
                    }
                )
            }
            else if (values.amount < 0) {
                errors.amount = intl.formatMessage(
                    {
                        id: 'contracts.account.payment.partial.amountNegative',
                        defaultMessage: 'Le montant ne peut pas être négatif',
                        description: 'Negative amount error'
                    }
                )
            } else if (`${values.amount}` === '0') {
                errors.amount = intl.formatMessage(
                    {
                        id: 'contracts.account.payment.partial.amountZero',
                        defaultMessage: 'Le montant ne peut pas être nul',
                        description: 'Nul amount error'
                    }
                )
            }
            // Le paiement partiel n'est pas autorisé : seul le montant restant dû est acceptable
            const montantSaisi = Number(values.amount.toString().replace(',', '.'))
            if (Math.abs(montantSaisi - paymentModal.amount) > 0.001) {
                errors.amount = intl.formatMessage(
                    {
                        id: 'contracts.account.payment.partial.amountExact',
                        defaultMessage: 'Le montant total de la facture doit être réglé',
                        description: 'Exact amount error'
                    }
                )
            }
        }

        return errors
    }

    const onSubmit = (values: PaymentFormModel) => {
        return payment(values.amount)
    }

    const getChild = () => {
        if (paymentModal.type === onlinePaymentMode.all) {
            return <div className={descriptionClass}>
                <div className={labelSoldeClass}>
                    <span className={classes.text1_opacity}>
                        <FormattedMessage
                            id="contracts.account.payment.all.description"
                            defaultMessage="Vous vous apprêtez à régler le montant de :"
                            description="Description payment"
                        />
                    </span>
                    <span className={infoClass}>
                        {paymentModal.amount.toLocaleString()} €
                    </span>
                </div>
                <div className={btnClass}>
                    {loading ? (
                        <div className={classes.spinnerStyle}>
                            <Loader />
                        </div>
                    ) : (<Button
                        background="backgroundAccessibility"
                        color="colorWhite"
                        textStyle="text8"
                        onClick={() => payment(paymentModal.amount)}
                    >
                        <FormattedMessage
                            id="contracts.account.payment.btn"
                            defaultMessage="Payer en ligne"
                            description="Payment button"
                        />
                    </Button>)
                    }
                </div>
            </div>
        } else {
            return <div className={descriptionClass}>
                <div className="col-xs-12">
                    <span className={classes.text1_opacity}>
                        <FormattedMessage
                            id="contracts.account.payment.partial.description1"
                            defaultMessage="Vous vous apprêtez à régler votre facture "
                            description="Description payment"
                        />
                    </span>
                    <span className={infoClass}>
                        {paymentModal.facture}
                    </span>
                </div>
                <div className="col-xs-12">
                    <span className={classes.text1_opacity}>
                        <FormattedMessage
                            id="contracts.account.payment.partial.description2"
                            defaultMessage="Le montant total doit être réglé en une seule fois."
                            description="Description input payment"
                        />
                    </span>
                </div>
                <div className="col-xs-12">
                    <PaymentForm
                        onSubmit={onSubmit}
                        validate={validate}
                        loading={loading}
                        initialValues={{
                            amount: paymentModal.amount,
                            num_bail: paymentModal.lease
                        }}
                    />
                </div>
            </div>
        }
    }

    return <Dialog
        onCloseDialog={closeDialog}
        open={paymentModal.open}
    >
        <>
            <PaymentHeader
                closeDialog={closeDialog}
                paymentModal={paymentModal}
            />
            <div className={childContainerClass}>
                {getChild()}
            </div>
        </>
    </Dialog>
}

export default PaymentDialog
